// --- PrintableLineup.tsx ---
import { AssignedPlayer, LookupDetails, PlayerInstance } from "@alienheadwars/football-card-manager-model";
import PrintIcon from '@mui/icons-material/Print';
import { Box, IconButton, Stack } from '@mui/material';
import React, { useRef } from "react";
import { useReactToPrint } from 'react-to-print';
import { Printable } from "../../../cards/playerCards/CardDimensions"; // Adjusted path
import { PlayerCardComponent } from "../../../cards/playerCards/PlayerCard"; // Adjusted path

interface LineupPrintableComponentProps {
    players: (AssignedPlayer | PlayerInstance | LookupDetails)[];
}

const LineupPrintableComponent = React.forwardRef<HTMLDivElement, LineupPrintableComponentProps>(({ players }, ref) => {
    return (
        <div ref={ref} style={{ backgroundColor: 'white' }}>
            <style type="text/css" media="print">{`
                @page {
                    size: landscape;
                    margin: 15mm;
                }
                html, body {
                    height: auto !important;
                    width: auto !important;
                    overflow: visible !important;
                    background-color: white !important;
                    color-adjust: exact !important;
                    -webkit-print-color-adjust: exact !important;
                    box-sizing: border-box !important;
                }
                body > div[id^="print-mount"], 
                body > div[data-react-to-print="true"],
                div[data-react-to-print="true"] {
                    height: auto !important;
                    width: auto !important;
                    overflow: visible !important;
                    box-sizing: border-box !important;
                }
                .printable-container-box { /* For the MUI Box parent of Stack */
                    padding: 8px;
                    background-color: white !important;
                    height: auto !important;
                    width: 100% !important; /* Box takes full width to define context for Stack's 100% width */
                    box-sizing: border-box !important;
                }
                .printable-stack-container { /* For the MUI Stack */
                    display: flex !important;
                    flex-direction: row !important; /* Horizontal layout */
                    flex-wrap: wrap !important;   /* Allow cards to wrap */
                    justify-content: flex-start; /* Align items to the start of each line */
                    align-items: flex-start;     /* Align items to the top of each line */
                    gap: 8px !important;         /* Consistent spacing */
                    height: auto !important;
                    width: 100% !important;      /* Stack uses full width of its parent Box */
                    box-sizing: border-box !important;
                }
                .player-card-print-item-wrapper { /* For the div around each PlayerCard */
                    page-break-inside: avoid !important; /* Try to keep card from splitting */
                    box-sizing: border-box;
                }
            `}</style>
            <Box className="printable-container-box">
                <Stack className="printable-stack-container">
                    {players.map((player) => (
                        <div
                            key={player.id}
                            className="player-card-print-item-wrapper"
                        >
                            <PlayerCardComponent
                                player={player}
                                cardDimensions={Printable}
                            />
                        </div>
                    ))}
                </Stack>
            </Box>
        </div>
    );
});
LineupPrintableComponent.displayName = 'LineupPrintableComponent';

interface PrintLineupButtonProps {
    players: (AssignedPlayer | PlayerInstance | LookupDetails)[];
}

export const PrintLineupButton = ({ players }: PrintLineupButtonProps) => {
    const printableContentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: printableContentRef,
        documentTitle: "Team Lineup",
        pageStyle: `
            @page {
                size: landscape; /* Ensure this is still set if not overridden by component styles */
            }
        `
    });

    return (
        <>
            <IconButton
                onClick={handlePrint}
                aria-label="print lineup"
                sx={{
                    position: 'absolute',
                    top: '1vmin',
                    right: '2vmin',
                    zIndex: 1001, // Ensure it's above the pitch but below modals if any
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                    padding: '1vmin'
                }}
                size="small"
            >
                <PrintIcon />
            </IconButton>
            <div style={{ display: 'none' }}>
                <LineupPrintableComponent
                    ref={printableContentRef}
                    players={players}
                />
            </div>
        </>
    );
};